export interface What3WordsSuggestion {
  words: string;
  distance?: number;
  country?: string;
  nearestPlace?: string;
}

interface What3WordsApiErrorResponse {
  error?: {
    message?: string;
  };
}

interface What3WordsAutosuggestResponse extends What3WordsApiErrorResponse {
  suggestions?: Array<{
    words?: string;
    country?: string;
    nearestPlace?: string;
    distanceToFocusKm?: number;
  }>;
}

interface What3WordsCoordinatesResponse extends What3WordsApiErrorResponse {
  coordinates?: {
    lat?: number;
    lng?: number;
  };
}

const WHAT3WORDS_API_BASE_URL = "https://api.what3words.com/v3";

const getWhat3WordsApiKey = () => import.meta.env.VITE_W3W_API_KEY?.trim();

const getRequiredWhat3WordsApiKey = () => {
  const apiKey = getWhat3WordsApiKey();

  if (!apiKey) {
    throw new Error("What3Words is not configured for web.");
  }

  return apiKey;
};

const buildWhat3WordsUrl = (
  path: string,
  params: Record<string, string | number>
) => {
  const url = new URL(`${WHAT3WORDS_API_BASE_URL}/${path}`);

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, String(value));
  });

  return url.toString();
};

const getWhat3WordsErrorMessage = (data: What3WordsApiErrorResponse) =>
  data.error?.message?.trim() || "What3Words request failed.";

const hasEnoughInputForWhat3WordsSearch = (value: string) =>
  value.length >= 3 && (value.match(/\./g) || []).length >= 2;

const isCompleteWhat3WordsAddress = (value: string) => {
  const segments = value.split(".", 3);

  return (
    segments.length === 3 &&
    segments.every((segment) => segment.trim().length > 0)
  );
};

export const autoSuggestWhat3Words = async (
  input: string
): Promise<What3WordsSuggestion[]> => {
  const trimmedInput = input.trim();

  if (!trimmedInput || !hasEnoughInputForWhat3WordsSearch(trimmedInput)) {
    return [];
  }

  const response = await fetch(
    buildWhat3WordsUrl("autosuggest", {
      input: trimmedInput,
      "clip-to-country": "GB",
      key: getRequiredWhat3WordsApiKey(),
    })
  );

  if (!response.ok) {
    throw new Error(`What3Words autosuggest failed: ${response.status}`);
  }

  const data = (await response.json()) as What3WordsAutosuggestResponse;

  if (data.error) {
    throw new Error(getWhat3WordsErrorMessage(data));
  }

  if (!Array.isArray(data.suggestions)) {
    return [];
  }

  return data.suggestions
    .filter(
      (
        suggestion
      ): suggestion is NonNullable<
        What3WordsAutosuggestResponse["suggestions"]
      >[number] & { words: string } => typeof suggestion.words === "string"
    )
    .map((suggestion) => ({
      words: suggestion.words,
      country: suggestion.country,
      nearestPlace: suggestion.nearestPlace,
      distance: suggestion.distanceToFocusKm,
    }));
};

export const convertWhat3WordsToCoordinates = async (words: string) => {
  const trimmedWords = words.trim();

  if (!trimmedWords || !isCompleteWhat3WordsAddress(trimmedWords)) {
    throw new Error("Enter a What3Words address.");
  }

  const response = await fetch(
    buildWhat3WordsUrl("convert-to-coordinates", {
      words: trimmedWords,
      key: getRequiredWhat3WordsApiKey(),
    })
  );

  if (!response.ok) {
    throw new Error(`What3Words lookup failed: ${response.status}`);
  }

  const data = (await response.json()) as What3WordsCoordinatesResponse;

  if (data.error) {
    throw new Error(getWhat3WordsErrorMessage(data));
  }

  if (
    typeof data.coordinates?.lat !== "number" ||
    typeof data.coordinates?.lng !== "number"
  ) {
    throw new Error("Invalid What3Words address.");
  }

  return {
    latitude: data.coordinates.lat,
    longitude: data.coordinates.lng,
  };
};
